import { Injectable } from '@angular/core';
import { ApolloError } from '@apollo/client/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { EMPTY, combineLatest, distinctUntilChanged, filter, map, switchMap } from 'rxjs';

import {
  DataOnlyGQL,
  GetConfigValuesGQL,
  PreviewReportQueryVariables,
  TransactionStage,
} from '../../../generated/graphql.generated';
import { GraphQLModule } from '../../core/public-api';
import { FRONTEND_PERMISSIONS } from '../../permissions.constants';
import { DemoModeService } from '../../services/demo-mode.service';
import { PermissionService } from '../../services/permission.service';
import { currentTimeSeconds } from '../../time';
import { getDashboardErrorMessage } from '../../utilities/errors.util';
import { parseEmbeddedUrl } from '../../utilities/generic.util';

import { DashboardStore } from '../dashboardv2.store';

export const transactionStatusOptions = [
  'Paid',
  'Pending',
  'Cancelled',
  'All',
] as const;

export type TransactionStatusOption = typeof transactionStatusOptions[number];

export const transactionAnchorOptions = [
  'Paid Date',
  'Created Date',
] as const;

export type TransactionAnchorOption = typeof transactionAnchorOptions[number];

const STATUS_STAGES: Record<TransactionStatusOption, TransactionStage[]> = {
  Paid: [ TransactionStage.Paid ],
  Pending: [ TransactionStage.Pending ],
  Cancelled: [ TransactionStage.Cancelled ],
  All: [],
};

export const ANCHORS: Record<TransactionAnchorOption, { periodType: string; dateColumn: string }> = {
  'Paid Date': { periodType: 'transactionPaidAt', dateColumn: 'transactionPaidDate' },
  'Created Date': { periodType: 'transactionCreatedAt', dateColumn: 'transactionCreatedDate' },
};

const TRANSACTION_COLUMNS = [
  'transactionCreatedDate',
  'transactionPaidDate',
  'job',
  'customer',
  'transactionStage',
  'transactionProvider',
  'transactionAmount',
  'transactionDifferential',
  'transactionTip',
];

const NUMERIC_COLUMNS = new Set([
  'transactionAmount',
  'transactionDifferential',
  'transactionTip',
]);

export function getTransactionsFilter(
  currency: string,
  status: TransactionStatusOption,
): PreviewReportQueryVariables['filter'] {
  return {
    job: {
      getJobsOfState: [ 'open', 'closed', 'archived' ],
      currency: currency === 'All' ? undefined : currency,
    },
    transaction: {
      stages: STATUS_STAGES[status],
    },
  };
}

export interface TransactionsTableCell {
  text: string;
  routerLink?: string;
  queryParams?: Record<string, string>;
  numeric: boolean;
}

const GATEWAY_PROVIDERS = [ 'bluesage', 'square' ];

interface ReportData {
  headers: string[];
  rows: string[][];
}

interface TransactionsState {
  status: TransactionStatusOption;
  anchor: TransactionAnchorOption;
  report: ReportData;
  loading: boolean;
  error: string | null;
  hasGateway: boolean | null;
}

const initialState: TransactionsState = {
  status: 'Paid',
  anchor: 'Paid Date',
  report: { headers: [], rows: [] },
  loading: false,
  error: null,
  hasGateway: null,
};

@Injectable()
export class DashboardTransactionsStore extends ComponentStore<TransactionsState> {

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly dataOnlyGQL: DataOnlyGQL,
    private readonly getConfigValuesGQL: GetConfigValuesGQL,
    private readonly graphqlModule: GraphQLModule,
    private readonly demoModeService: DemoModeService,
    private readonly permissionService: PermissionService,
  ) {
    super(initialState);
  }

  readonly setStatus = this.updater((state, status: TransactionStatusOption): TransactionsState => ({ ...state, status }));

  readonly setAnchor = this.updater((state, anchor: TransactionAnchorOption): TransactionsState => ({ ...state, anchor }));

  readonly status$ = this.select((state) => state.status);

  readonly anchor$ = this.select((state) => state.anchor);

  private readonly queryVars$ = this.select(
    this.dashboardStore.currency$,
    this.dashboardStore.unixPeriod$,
    this.status$,
    this.anchor$,
    this.graphqlModule.zone,
    (currency, { start, end }, status, anchor, zone) => {
      if (!zone) return null;
      return this.getQueryVars({ start, end, currency, status, anchor });
    },
    { debounce: true },
  ).pipe(filter(Boolean));

  private readonly reloadTrigger$ = combineLatest([
    this.queryVars$,
    this.dashboardStore.routeActivated$,
    this.dashboardStore.reloadKey$,
  ]).pipe(
    filter(([ , path ]) => path === 'payments'),
    map(([ queryVars ]) => queryVars),
  );

  readonly reload = this.effect(() => combineLatest([
    this.reloadTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ queryVars, isDemo ]) => {
      if (isDemo) {
        this.patchState({ report: { headers: [], rows: [] }, loading: false, error: null });
        return EMPTY;
      }
      this.patchState({ loading: true, error: null });
      return this.dataOnlyGQL.fetch(queryVars, { fetchPolicy: 'network-only' }).pipe(tapResponse({
        next: (res) => {
          const { headers, rows } = res.data.previewReport.data;
          this.patchState({ report: { headers, rows }, loading: false });
          this.dashboardStore.setLastReload(currentTimeSeconds());
        },
        error: (err: ApolloError) => {
          this.patchState({ loading: false, error: getDashboardErrorMessage(err) });
        },
      }));
    }),
  ));

  readonly loadGateway = this.effect(() => combineLatest([
    this.graphqlModule.zone.pipe(filter(Boolean), distinctUntilChanged()),
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ , isDemo ]) => {
      this.patchState({ hasGateway: null });
      if (isDemo) return EMPTY;
      return this.getConfigValuesGQL.fetch({ keys: [ 'payment-type.*' ] }, { fetchPolicy: 'network-only' }).pipe(tapResponse({
        next: (res) => this.patchState({ hasGateway: this.hasGatewayPaymentType(res.data.getConfigValues) }),
        error: () => this.patchState({ hasGateway: null }),
      }));
    }),
  ));

  private readonly queryParams$ = this.select(
    this.queryVars$,
    this.dashboardStore.timezone$,
    (queryVars, timezone) => this.dashboardStore.queryVarsToQueryParams(queryVars, timezone),
  );

  private readonly rows$ = this.select((state) => state.report.rows.map((row) => row.map((raw, i): TransactionsTableCell => {
    const numeric = NUMERIC_COLUMNS.has(TRANSACTION_COLUMNS[i]);
    const link = parseEmbeddedUrl(raw);
    if (link) {
      return { text: link.before, routerLink: link.url, queryParams: link.queryParams, numeric };
    }
    return { text: raw === 'None' ? '' : raw, numeric };
  })));

  readonly viewModel$ = this.select(
    this.state$,
    this.rows$,
    this.queryParams$,
    this.dashboardStore.lastReload$,
    this.permissionService.watchPermission(FRONTEND_PERMISSIONS.settings),
    (state, rows, queryParams, lastReload, canConfigureGateway) => ({
      status: state.status,
      anchor: state.anchor,
      loading: state.loading,
      error: state.error,
      headers: state.report.headers.map((header, i) => ({
        text: header,
        numeric: NUMERIC_COLUMNS.has(TRANSACTION_COLUMNS[i]),
      })),
      rows,
      isEmpty: !state.loading && !rows.length,
      queryParams,
      lastReload,
      showGatewayBanner: state.hasGateway === false,
      canConfigureGateway,
    }),
  );

  private hasGatewayPaymentType(configs: { value?: string }[] | undefined) {
    return (configs ?? []).some((config) => {
      try {
        const paymentType = JSON.parse(config?.value ?? '{}');
        return Boolean(paymentType?.enabled) && GATEWAY_PROVIDERS.includes(paymentType?.provider);
      } catch {
        return false;
      }
    });
  }

  private getQueryVars(input: {
    start: number;
    end: number;
    currency: string;
    status: TransactionStatusOption;
    anchor: TransactionAnchorOption;
  }): PreviewReportQueryVariables {
    const { periodType, dateColumn } = ANCHORS[input.anchor];

    return {
      skip: 0,
      limit: -1,
      start: input.start,
      end: input.end,
      periodType,
      groupBy: 'transaction',
      filter: getTransactionsFilter(input.currency, input.status),
      dataColumns: TRANSACTION_COLUMNS,
      aggregationColumns: [],
      sort: `${ dateColumn } DESC`,
    };
  }
}
